/**
 * Nifty 50 Panel Component
 * Shows all Nifty 50 symbols with live LTP and %Change via WebSocket
 * Reads prices DIRECTLY from livePrices prop (same as OptionChainFrame)
 * Uses sessionStorage cache for instant symbol list + fallback prices
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import aliceBlueWS from '../../services/aliceBlueWebSocket';
import api from '../../services/api';

const NIFTY50_CACHE_KEY = 'nifty50_symbols_cache';

// Helper: load cached symbols from sessionStorage
const loadCachedSymbols = () => {
  try {
    const cached = sessionStorage.getItem(NIFTY50_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return [];
};

const Nifty50Panel = ({ livePrices, wsConnected, onSymbolSelect }) => {
  const { userSession } = useSelector((state) => state.auth);

  // Load cached symbols instantly from sessionStorage
  const cachedOnMount = useRef(loadCachedSymbols());

  // Symbol metadata (no prices stored here - prices come from livePrices prop directly)
  const [symbolList, setSymbolList] = useState(cachedOnMount.current);

  // Only show loading spinner if we have NO cached data at all
  const [loading, setLoading] = useState(cachedOnMount.current.length === 0);

  const [sortConfig, setSortConfig] = useState({ key: 'changePercent', direction: 'desc' });
  const [error, setError] = useState(null);
  const subscriptionSentRef = useRef(false);

  // Snapshot state for Dif column
  const [snapshotMap, setSnapshotMap] = useState(null);
  const [snapshotTime, setSnapshotTime] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // === KEY FIX: Read prices directly from livePrices prop at render time ===
  // Same pattern as OptionChainFrame.getLivePrice() - no useEffect/setState intermediary
  // This means prices appear on the SAME render cycle, not one cycle later
  const displaySymbols = useMemo(() => {
    return symbolList.map(s => {
      const key = `${s.exchange || 'NSE'}|${s.token}`;
      const priceData = livePrices?.[key];

      // Read live price directly from parent's livePrices (instant, same render)
      const wsLtp = priceData?.ltp || priceData?.lp;
      const wsPc = priceData?.pc !== undefined ? parseFloat(priceData.pc) : null;

      return {
        ...s,
        // Priority: WebSocket live > cached/API price > null
        ltp: (wsLtp !== undefined && wsLtp !== null && wsLtp > 0) ? wsLtp : (s.ltp || null),
        changePercent: wsPc !== null ? wsPc : (s.changePercent ?? null)
      };
    });
  }, [symbolList, livePrices]);

  // Handle symbol double-click to add to option chain position
  const handleSymbolDoubleClick = (symbol) => {
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
    }
  };

  // Fetch Nifty 50 symbols from backend (background refresh if cache exists)
  const fetchNifty50Symbols = useCallback(async () => {
    if (!userSession) return;

    try {
      const hasCachedData = symbolList.length > 0;
      if (!hasCachedData) setLoading(true);
      setError(null);

      const response = await api.get('/nse-options-trade/nifty50-symbols', {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success && response.data.symbols) {
        const symbols = response.data.symbols;

        // Cache symbol metadata WITH prices for fallback on next load
        const symbolsForCache = symbols.map(s => ({
          id: s.id,
          companyName: s.companyName,
          symbol: s.symbol,
          exchange: s.exchange,
          token: s.token,
          ltp: s.ltp || null,
          changePercent: s.changePercent || null,
          prevClose: s.prevClose || null
        }));

        setSymbolList(symbolsForCache);

        try {
          sessionStorage.setItem(NIFTY50_CACHE_KEY, JSON.stringify(symbolsForCache));
        } catch (e) { /* ignore */ }
      } else if (symbolList.length === 0) {
        setError('No symbols found');
      }
    } catch (err) {
      console.error('❌ [Nifty 50] Failed to fetch symbols:', err);
      if (symbolList.length === 0) setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession]);

  // Fetch symbols on mount (background refresh if cache exists)
  useEffect(() => {
    fetchNifty50Symbols();
  }, [fetchNifty50Symbols]);

  // Subscribe to WebSocket for all Nifty 50 symbols
  useEffect(() => {
    if (symbolList.length > 0 && wsConnected && !subscriptionSentRef.current) {
      const instruments = symbolList
        .filter(s => s.token)
        .map(s => ({
          exchange: s.exchange || 'NSE',
          token: s.token
        }));

      if (instruments.length > 0) {
        aliceBlueWS.subscribe(instruments);
        subscriptionSentRef.current = true;
        console.log(`📡 [Nifty 50] Subscribed ${instruments.length} symbols`);
      }
    }
  }, [symbolList, wsConnected]);

  // Periodically save current live prices to sessionStorage cache
  useEffect(() => {
    if (displaySymbols.length === 0) return;
    const interval = setInterval(() => {
      const withPrices = displaySymbols.filter(s => s.ltp !== null);
      if (withPrices.length > 20) {
        try {
          const toCache = displaySymbols.map(s => ({
            id: s.id,
            companyName: s.companyName,
            symbol: s.symbol,
            exchange: s.exchange,
            token: s.token,
            ltp: s.ltp || null,
            changePercent: s.changePercent ?? null,
            prevClose: s.prevClose || null
          }));
          sessionStorage.setItem(NIFTY50_CACHE_KEY, JSON.stringify(toCache));
        } catch (e) { /* ignore */ }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [displaySymbols]);

  // Load existing snapshot on mount
  useEffect(() => {
    const loadSnapshot = async () => {
      try {
        const response = await api.get('/nse-options-trade/nifty50-snapshot', {
          headers: { Authorization: `Bearer ${userSession}` }
        });
        if (response.data.success && response.data.snapshot) {
          setSnapshotMap(response.data.snapshot);
          setSnapshotTime(response.data.snapshotTime);
        }
      } catch (err) {
        console.error('Failed to load snapshot:', err);
      }
    };
    if (userSession) loadSnapshot();
  }, [userSession]);

  // Handle Analyse button click - save current LTPs as snapshot
  const handleAnalyse = async () => {
    if (displaySymbols.length === 0) return;
    setSnapshotLoading(true);
    try {
      const symbolsToSave = displaySymbols
        .filter(s => s.ltp !== null && s.ltp !== undefined && s.token)
        .map(s => ({
          symbol: s.symbol,
          token: String(s.token),
          exchange: s.exchange || 'NSE',
          ltp: parseFloat(s.ltp)
        }));

      const response = await api.post('/nse-options-trade/nifty50-snapshot',
        { symbols: symbolsToSave },
        { headers: { Authorization: `Bearer ${userSession}` } }
      );

      if (response.data.success) {
        const newMap = {};
        symbolsToSave.forEach(s => { newMap[s.token] = s.ltp; });
        setSnapshotMap(newMap);
        setSnapshotTime(response.data.timestamp);
      }
    } catch (err) {
      console.error('Failed to save snapshot:', err);
    } finally {
      setSnapshotLoading(false);
    }
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted symbols
  const sortedSymbols = useMemo(() => {
    return [...displaySymbols].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal, bVal;

      switch (key) {
        case 'symbol':
          aVal = a.symbol || '';
          bVal = b.symbol || '';
          return direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'ltp':
          aVal = a.ltp || 0;
          bVal = b.ltp || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'changePercent':
          aVal = a.changePercent || 0;
          bVal = b.changePercent || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        case 'dif':
          aVal = (snapshotMap && a.token && a.ltp !== null && snapshotMap[String(a.token)] !== undefined)
            ? parseFloat(a.ltp) - snapshotMap[String(a.token)] : 0;
          bVal = (snapshotMap && b.token && b.ltp !== null && snapshotMap[String(b.token)] !== undefined)
            ? parseFloat(b.ltp) - snapshotMap[String(b.token)] : 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        default:
          return 0;
      }
    });
  }, [displaySymbols, sortConfig, snapshotMap]);

  // Sort indicator
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <span className="text-white/30 ml-0.5">↕</span>;
    return (
      <span className="text-yellow-400 ml-0.5">
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const liveCount = sortedSymbols.filter(s => s.ltp !== null).length;

  return (
    <div className="h-full flex flex-col gap-1 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-1.5 border border-white/20">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-sm">
            📊 Nifty 50 {loading && <span className="text-xs text-white/60 ml-1">(loading...)</span>}
          </h2>
          <div className="flex items-center gap-1">
            <span className="text-white/60 text-xs">
              {liveCount}/{symbolList.length} live
            </span>
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`} />
            <button
              onClick={fetchNifty50Symbols}
              className="bg-white/20 hover:bg-white/30 text-white px-2 py-0.5 rounded-lg text-xs transition-all"
              disabled={loading}
            >
              {loading ? '⟳' : '🔄'}
            </button>
          </div>
        </div>
      </div>

      {/* Nifty 50 Table */}
      <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          {error ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-red-400 text-sm">Error: {error}</span>
            </div>
          ) : symbolList.length === 0 && loading ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-white/60 text-sm">Loading Nifty 50 symbols...</span>
            </div>
          ) : symbolList.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <span className="text-white/60 text-sm">No symbols available</span>
            </div>
          ) : (
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 bg-gray-800/90 text-white z-10">
                <tr>
                  <th
                    className="text-left px-2 py-1.5 font-semibold cursor-pointer hover:text-yellow-300 select-none" style={{ width: '30%' }}
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol <SortIcon columnKey="symbol" />
                  </th>
                  <th
                    className="text-right px-1.5 py-1.5 font-semibold cursor-pointer hover:text-yellow-300 select-none" style={{ width: '25%' }}
                    onClick={() => handleSort('ltp')}
                  >
                    LTP <SortIcon columnKey="ltp" />
                  </th>
                  <th
                    className="text-right px-1.5 py-1.5 font-semibold cursor-pointer hover:text-yellow-300 select-none" style={{ width: '22%' }}
                    onClick={() => handleSort('changePercent')}
                  >
                    Chg <SortIcon columnKey="changePercent" />
                  </th>
                  <th
                    className="text-right px-1 py-1.5 font-semibold select-none"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span
                        className="cursor-pointer hover:text-yellow-300"
                        onClick={() => handleSort('dif')}
                      >
                        Dif <SortIcon columnKey="dif" />
                      </span>
                      <button
                        onClick={handleAnalyse}
                        disabled={snapshotLoading || symbolList.length === 0}
                        className={`p-0.5 rounded transition-all ${
                          snapshotLoading
                            ? 'text-yellow-400 animate-spin'
                            : snapshotMap
                              ? 'text-green-400 hover:text-green-300 hover:bg-white/10'
                              : 'text-white/60 hover:text-yellow-300 hover:bg-white/10'
                        }`}
                        title={snapshotMap
                          ? `Snapshot taken at ${snapshotTime ? new Date(snapshotTime).toLocaleTimeString() : 'unknown'} - Click to refresh`
                          : 'Take LTP snapshot for Dif calculation'}
                      >
                        {snapshotLoading ? '⟳' : '📸'}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedSymbols.map((stock) => {
                  const isPositive = stock.changePercent !== null && stock.changePercent >= 0;

                  return (
                    <tr
                      key={`nifty50-${stock.token || stock.symbol}`}
                      className="border-b border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                      onDoubleClick={() => handleSymbolDoubleClick(stock.symbol)}
                      title={`${stock.companyName} - Double-click to add to position`}
                    >
                      <td className="px-2 py-1.5 text-white font-semibold truncate overflow-hidden">
                        {stock.symbol}
                      </td>
                      <td className="px-1.5 py-1.5 text-right text-white font-mono font-semibold">
                        {stock.ltp !== null
                          ? parseFloat(stock.ltp).toFixed(2)
                          : <span className="text-white/30">--</span>
                        }
                      </td>
                      <td className="px-1.5 py-1.5 text-right">
                        {stock.changePercent !== null ? (
                          <span className={`font-bold font-mono text-xs ${
                            isPositive ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {isPositive ? '+' : ''}{stock.changePercent.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-white/30">--</span>
                        )}
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        {(() => {
                          if (!snapshotMap || !stock.token || stock.ltp === null || stock.ltp === undefined) {
                            return <span className="text-white/20">--</span>;
                          }
                          const snapLtp = snapshotMap[String(stock.token)];
                          if (snapLtp === undefined) {
                            return <span className="text-white/20">--</span>;
                          }
                          const diff = parseFloat(stock.ltp) - snapLtp;
                          const isDiffPositive = diff >= 0;
                          return (
                            <span className={`font-bold font-mono text-xs ${
                              isDiffPositive ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {isDiffPositive ? '+' : ''}{diff.toFixed(2)}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Nifty50Panel;
